/**
 * Mock Data for Initial Tasks
 * 
 * Provides sample tasks for development and testing purposes.
 */

import { Task, TaskStatus, TaskPriority } from '../types';
import { ensureTasksHaveOrder } from './taskHelpers';

// Raw mock data without order field (for easier maintenance)
const rawMockTasks = [
  {
    id: '1',
    title: 'Implement authentication',
    description: 'Implement JWT-based authentication system with refresh tokens, secure password hashing using bcrypt, and role-based access control. Include email verification and password reset functionality.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Nikhil Nagar',
    tags: ['backend', 'security'],
    createdAt: new Date('2024-11-20T10:00:00Z'),
    updatedAt: new Date('2024-11-20T10:00:00Z')
  },
  {
    id: '2',
    title: 'Design new landing page',
    description: 'Create comprehensive mockups and wireframes for the homepage redesign. Focus on modern UI/UX principles, improved conversion rates, and mobile-first approach. Include hero section, feature highlights, testimonials, and clear CTAs.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    assignee: 'Sangamesh Sangalad',
    tags: ['design', 'frontend'],
    createdAt: new Date('2024-11-19T14:30:00Z'),
    updatedAt: new Date('2024-11-19T14:30:00Z')
  },
  {
    id: '3',
    title: 'Fix payment gateway bug',
    description: 'Critical bug where users are unable to complete checkout process. Payment gateway returns timeout errors intermittently. Need to investigate API integration, add proper error handling, and implement retry logic with exponential backoff.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Nikhil Nagar',
    tags: ['backend', 'urgent'],
    createdAt: new Date('2024-11-21T09:15:00Z'),
    updatedAt: new Date('2024-11-21T09:15:00Z'),
 
  },
  {
    id: '4',
    title: 'Update user documentation',
    description: 'Comprehensive revision of API documentation for version 2.0 release. Update all endpoints, add code examples in multiple languages, document breaking changes, and create migration guide from v1.x to v2.0.',
    status: TaskStatus.TODO,
    priority: TaskPriority.LOW,
    assignee: 'Shirsha Chaudhuri',
    tags: ['documentation', 'api'],
    createdAt: new Date('2024-11-18T11:00:00Z'),
    updatedAt: new Date('2024-11-18T11:00:00Z'),
  },
  {
    id: '5',
    title: 'Optimize database queries',
    description: 'User search queries are taking 3-5 seconds to execute. Profile slow queries, add appropriate indexes, implement query result caching, and consider denormalization for frequently accessed data. Target sub-second response times.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.HIGH,
    assignee: 'Bob Johnson',
    tags: ['backend', 'performance'],
    createdAt: new Date('2024-11-20T08:30:00Z'),
    updatedAt: new Date('2024-11-20T08:30:00Z'),
  },
  {
    id: '6',
    title: 'Create mobile responsive design',
    description: 'Ensure all pages are fully responsive and work seamlessly on mobile devices (iOS and Android). Test on various screen sizes, implement touch-friendly interactions, optimize images for mobile bandwidth, and ensure text readability.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'mobile', 'design'],
    createdAt: new Date('2024-11-17T16:45:00Z'),
    updatedAt: new Date('2024-11-17T16:45:00Z'),
  },
  {
    id: '7',
    title: 'Set up CI/CD pipeline',
    description: 'Configure comprehensive CI/CD pipeline using GitHub Actions. Include automated testing (unit, integration, e2e), code quality checks, security scanning, automated deployments to staging and production environments with approval gates.',
    status: TaskStatus.DONE,
    priority: TaskPriority.HIGH,
    assignee: 'Bob Johnson',
    tags: ['devops', 'infrastructure'],
    createdAt: new Date('2024-11-15T10:00:00Z'),
    updatedAt: new Date('2024-11-16T14:20:00Z'),
  },
  {
    id: '8',
    title: 'Implement dark mode',
    description: 'Add dark theme toggle to the application with smooth transitions. Implement theme persistence using localStorage, ensure all components support both themes, maintain proper contrast ratios for accessibility, and add system preference detection.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.LOW,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'ui', 'feature'],
    createdAt: new Date('2024-11-19T09:00:00Z'),
    updatedAt: new Date('2024-11-19T09:00:00Z'),
  },
  {
    id: '9',
    title: 'Add email notifications',
    description: 'Implement email notification system for important events such as password resets, account changes, payment confirmations, and weekly activity summaries. Use SendGrid API, create responsive email templates, and add unsubscribe functionality.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Nikhil Nagar',
    tags: ['backend', 'notifications'],
    createdAt: new Date('2024-11-21T13:20:00Z'),
    updatedAt: new Date('2024-11-21T13:20:00Z'),
  },
  {
    id: '10',
    title: 'Refactor authentication module',
    description: 'Clean up authentication codebase to improve maintainability and testability. Extract reusable functions, add comprehensive error handling, improve type safety, write unit tests, and update documentation. Follow SOLID principles.',
    status: TaskStatus.TODO,
    priority: TaskPriority.LOW,
    assignee: 'Bob Johnson',
    tags: ['backend', 'refactoring'],
    createdAt: new Date('2024-11-16T15:30:00Z'),
    updatedAt: new Date('2024-11-16T15:30:00Z'),
  },
  {
    id: '11',
    title: 'Create admin dashboard',
    description: 'Build comprehensive analytics dashboard for administrators with real-time metrics, user activity tracking, revenue charts, system health monitoring, and customizable widgets. Include data export functionality and role-based access control.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    assignee: 'Shirsha Chaudhuri',
    tags: ['frontend', 'admin', 'feature'],
    createdAt: new Date('2024-11-18T10:15:00Z'),
    updatedAt: new Date('2024-11-18T10:15:00Z'),
  },
  {
    id: '12',
    title: 'Fix CSS styling issues',
    description: 'Several layout problems reported on Safari browser including misaligned buttons, broken flexbox layouts, and incorrect z-index stacking. Test across Safari versions, add vendor prefixes where needed, and ensure cross-browser compatibility.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'bugfix', 'css'],
    createdAt: new Date('2024-11-20T14:00:00Z'),
    updatedAt: new Date('2024-11-20T14:00:00Z'),
  },
  {
    id: '13',
    title: 'Implement file upload feature',
    description: 'Allow users to upload profile pictures and documents. Implement drag-and-drop interface, file type validation, size limits (max 5MB), image compression, secure storage using AWS S3, and generate thumbnails for images. Add progress indicators.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Nikhil Nagar',
    tags: ['backend', 'feature'],
    createdAt: new Date('2024-11-21T11:45:00Z'),
    updatedAt: new Date('2024-11-21T11:45:00Z'),
  },
  {
    id: '14',
    title: 'Write integration tests',
    description: 'Create comprehensive end-to-end tests for critical user flows including registration, login, checkout process, and account management. Use Cypress or Playwright, set up test data fixtures, and integrate with CI/CD pipeline for automated testing.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Bob Johnson',
    tags: ['testing', 'quality'],
    createdAt: new Date('2024-11-17T09:30:00Z'),
    updatedAt: new Date('2024-11-17T09:30:00Z'),
  },
  {
    id: '15',
    title: 'Update dependencies',
    description: 'Upgrade all npm packages to their latest stable versions. Review breaking changes, update code accordingly, run full test suite, check for security vulnerabilities, and update package-lock.json. Document any API changes that affect the codebase.',
    status: TaskStatus.DONE,
    priority: TaskPriority.LOW,
    assignee: 'Shirsha Chaudhuri',
    tags: ['maintenance', 'dependencies'],
    createdAt: new Date('2024-11-14T16:00:00Z'),
    updatedAt: new Date('2024-11-15T10:30:00Z'),
  },
  {
    id: '16',
    title: 'Add search functionality',
    description: 'Implement full-text search across all content using Elasticsearch. Include fuzzy matching, relevance scoring, faceted search, search suggestions, and result highlighting. Optimize for performance with proper indexing and caching strategies.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.HIGH,
    assignee: 'Nikhil Nagar',
    tags: ['backend', 'feature', 'search'],
    createdAt: new Date('2024-11-19T12:00:00Z'),
    updatedAt: new Date('2024-11-19T12:00:00Z'),
  },
  {
    id: '17',
    title: 'Design error pages',
    description: 'Create visually appealing and user-friendly 404 and 500 error page designs. Include helpful messaging, navigation options to return home, search functionality, and maintain brand consistency. Add illustrations or animations to improve user experience.',
    status: TaskStatus.TODO,
    priority: TaskPriority.LOW,
    assignee: 'Sangamesh Sangalad',
    tags: ['design', 'frontend'],
    createdAt: new Date('2024-11-18T14:20:00Z'),
    updatedAt: new Date('2024-11-18T14:20:00Z'),
  },
  {
    id: '18',
    title: 'Implement rate limiting',
    description: 'Add API rate limiting to prevent abuse and DDoS attacks. Implement token bucket algorithm, configure different limits for authenticated vs anonymous users, add proper HTTP headers (X-RateLimit-*), and return meaningful error messages when limits are exceeded.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Bob Johnson',
    tags: ['backend', 'security'],
    createdAt: new Date('2024-11-21T10:30:00Z'),
    updatedAt: new Date('2024-11-21T10:30:00Z'),
  },
  {
    id: '19',
    title: 'Create user onboarding flow',
    description: 'Design and implement interactive welcome tutorial for new users. Include step-by-step walkthrough of key features, tooltips, progress indicators, ability to skip or revisit later, and track completion analytics. Make it engaging and informative.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    assignee: 'Shirsha Chaudhuri',
    tags: ['frontend', 'ux', 'feature'],
    createdAt: new Date('2024-11-17T11:15:00Z'),
    updatedAt: new Date('2024-11-17T11:15:00Z'),
  },
  {
    id: '20',
    title: 'Fix memory leak in dashboard',
    description: 'Critical memory leak in dashboard causing browser slowdown and eventual crashes after extended use. Profile memory usage, identify leaking components, fix event listener cleanup, properly dispose of subscriptions, and add memory monitoring to prevent future leaks.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Nikhil Nagar',
    tags: ['frontend', 'bugfix', 'performance'],
    createdAt: new Date('2024-11-21T15:00:00Z'),
    updatedAt: new Date('2024-11-21T15:00:00Z'),
  },
  {
    id: '21',
    title: 'Add social media integration',
    description: 'Enable social login with Google, Facebook, and GitHub using OAuth 2.0. Implement secure token handling, account linking for existing users, profile data synchronization, and proper error handling for failed authentication attempts.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Bob Johnson',
    tags: ['backend', 'feature', 'oauth'],
    createdAt: new Date('2024-11-16T13:45:00Z'),
    updatedAt: new Date('2024-11-16T13:45:00Z'),
  },
  {
    id: '22',
    title: 'Optimize image loading',
    description: 'Implement lazy loading for images to improve initial page load time. Use Intersection Observer API, add loading placeholders, implement progressive image loading, serve WebP format with fallbacks, and add proper alt text for accessibility.',
    status: TaskStatus.DONE,
    priority: TaskPriority.MEDIUM,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'performance'],
    createdAt: new Date('2024-11-15T09:00:00Z'),
    updatedAt: new Date('2024-11-16T11:20:00Z'),
  },
  {
    id: '23',
    title: 'Create API versioning strategy',
    description: 'Plan and implement comprehensive API version management strategy. Support multiple API versions simultaneously, implement version deprecation policy, create migration guides, use semantic versioning, and ensure backward compatibility where possible.',
    status: TaskStatus.TODO,
    priority: TaskPriority.LOW,
    assignee: 'Shirsha Chaudhuri',
    tags: ['backend', 'api', 'planning'],
    createdAt: new Date('2024-11-18T15:30:00Z'),
    updatedAt: new Date('2024-11-18T15:30:00Z'),
  },
  {
    id: '24',
    title: 'Add analytics tracking',
    description: 'Integrate Google Analytics 4 and implement custom event tracking for user interactions. Track page views, button clicks, form submissions, conversion funnels, and user journeys. Set up custom dashboards and automated reports for stakeholders.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    assignee: 'Nikhil Nagar',
    tags: ['frontend', 'analytics'],
    createdAt: new Date('2024-11-19T16:00:00Z'),
    updatedAt: new Date('2024-11-19T16:00:00Z'),
  },
  {
    id: '25',
    title: 'Implement data export feature',
    description: 'Allow users to export their data in CSV and JSON formats for backup and portability. Include all user-generated content, apply proper data formatting, add progress indicators for large exports, and send download link via email for large files.',
    status: TaskStatus.TODO,
    priority: TaskPriority.LOW,
    assignee: 'Bob Johnson',
    tags: ['backend', 'feature'],
    createdAt: new Date('2024-11-17T14:00:00Z'),
    updatedAt: new Date('2024-11-17T14:00:00Z'),
  },
  {
    id: '26',
    title: 'Fix navigation menu bug',
    description: 'Navigation menu not closing properly on mobile devices when clicking outside or selecting a menu item. Investigate touch event handling, add proper event listeners, implement backdrop click detection, and test across iOS and Android devices.',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'bugfix', 'mobile'],
    createdAt: new Date('2024-11-21T08:30:00Z'),
    updatedAt: new Date('2024-11-21T08:30:00Z'),
  },
  {
    id: '27',
    title: 'Set up monitoring and alerts',
    description: 'Configure comprehensive application monitoring using Datadog or New Relic. Set up alerts for error rates, response times, server resources, and business metrics. Create dashboards for real-time monitoring and integrate with Slack for instant notifications.',
    status: TaskStatus.DONE,
    priority: TaskPriority.HIGH,
    assignee: 'Shirsha Chaudhuri',
    tags: ['devops', 'monitoring'],
    createdAt: new Date('2024-11-14T10:00:00Z'),
    updatedAt: new Date('2024-11-15T16:45:00Z'),
  },
  {
    id: '28',
    title: 'Create component library',
    description: 'Build reusable UI component library with consistent styling, comprehensive documentation, and usage examples. Include buttons, forms, modals, cards, and navigation components. Use Storybook for component showcase and testing in isolation.',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    assignee: 'Sangamesh Sangalad',
    tags: ['frontend', 'ui', 'library'],
    createdAt: new Date('2024-11-18T13:00:00Z'),
    updatedAt: new Date('2024-11-18T13:00:00Z'),
  },
  {
    id: '29',
    title: 'Implement caching strategy',
    description: 'Add Redis caching layer for frequently accessed data to reduce database load and improve response times. Implement cache invalidation strategies, set appropriate TTLs, add cache warming for critical data, and monitor cache hit rates for optimization.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Bob Johnson',
    tags: ['backend', 'performance', 'caching'],
    createdAt: new Date('2024-11-20T11:30:00Z'),
    updatedAt: new Date('2024-11-20T11:30:00Z'),
  },
  {
    id: '30',
    title: 'Add accessibility features',
    description: 'Ensure full WCAG 2.1 AA compliance across the application. Add proper ARIA labels, keyboard navigation support, screen reader compatibility, sufficient color contrast, focus indicators, and skip navigation links. Conduct accessibility audit and fix all issues.',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assignee: 'Shirsha Chaudhuri',
    tags: ['frontend', 'accessibility', 'a11y'],
    createdAt: new Date('2024-11-19T10:00:00Z'),
    updatedAt: new Date('2024-11-19T10:00:00Z'),
  },
] as Omit<Task, 'order'>[];

// Export with auto-assigned orders
export const mockTasks: Task[] = ensureTasksHaveOrder(rawMockTasks as Task[]);

export const mockAssignees = ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Williams'];
